import React, { useState, useRef, useCallback } from 'react'
import {
	DndContext,
	DragOverlay,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors,
	KeyboardSensor,
} from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import { Box, Flex, Text, Badge, useColorModeValue } from '@chakra-ui/react'

import SortableGameTile from './SortableGameTile'
import GameTilePreview from './GameTilePreview'
import { TIER_META, DROP_POOL, DROP_EXCLUDED, dropIdForTier, moveItemTowardOver, cloneRankState } from './rankingState'
import { rankingCollisionDetection } from './dndCollision'

function DroppableRow({ id, children, itemCount, bg, borderColor }) {
	const { setNodeRef, isOver } = useDroppable({ id })
	const minH = itemCount === 0 ? '72px' : undefined

	return (
		<Flex
			ref={setNodeRef}
			direction="row"
			flexWrap="nowrap"
			alignItems="stretch"
			overflowX="auto"
			overflowY="hidden"
			width="100%"
			maxW="100%"
			gap={2}
			minH={minH}
			p={2}
			pb={3}
			rounded="lg"
			borderWidth="1px"
			borderStyle="dashed"
			borderColor={isOver ? 'brand.400' : borderColor}
			bg={isOver ? 'brand.50' : bg}
			_dark={{
				bg: isOver ? 'whiteAlpha.100' : bg,
				borderColor: isOver ? 'brand.300' : borderColor,
			}}
			transition="border-color 0.15s, background 0.15s"
			sx={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
		>
			{children}
		</Flex>
	)
}

export default function TierBoard({ rankState, setRankState, itemsById }) {
	const [activeId, setActiveId] = useState(null)
	const rankSnapshotRef = useRef(null)

	const rowLabelColor = useColorModeValue('gray.600', 'gray.400')
	const rowBg = useColorModeValue('gray.50', 'bgg.900')
	const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300')

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: { distance: 8 },
		}),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 120, tolerance: 8 },
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	)

	const activeItem = activeId ? itemsById[String(activeId)] : null

	const handleDragStart = useCallback(
		({ active }) => {
			setActiveId(active.id != null ? String(active.id) : null)
			rankSnapshotRef.current = cloneRankState(rankState)
		},
		[rankState]
	)

	const handleDragCancel = useCallback(() => {
		setActiveId(null)
		if (rankSnapshotRef.current) {
			setRankState(rankSnapshotRef.current)
			rankSnapshotRef.current = null
		}
	}, [setRankState])

	const handleDragOver = useCallback(
		({ active, over }) => {
			if (!over) return
			const a = active.id != null ? String(active.id) : ''
			const o = over.id != null ? String(over.id) : ''
			if (!a || !o) return
			setRankState((prev) => moveItemTowardOver(prev, a, o))
		},
		[setRankState]
	)

	const handleDragEnd = useCallback(
		({ active, over }) => {
			setActiveId(null)
			const a = active.id != null ? String(active.id) : ''
			if (!over) {
				if (rankSnapshotRef.current) setRankState(rankSnapshotRef.current)
				rankSnapshotRef.current = null
				return
			}
			const o = over.id != null ? String(over.id) : ''
			rankSnapshotRef.current = null
			if (!a || !o) return
			setRankState((prev) => moveItemTowardOver(prev, a, o))
		},
		[setRankState]
	)

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={rankingCollisionDetection}
			onDragStart={handleDragStart}
			onDragOver={handleDragOver}
			onDragCancel={handleDragCancel}
			onDragEnd={handleDragEnd}
		>
			<Box mb={{ base: 3, md: 4 }}>
				<Text fontWeight="600" fontSize="sm" mb={2} color={rowLabelColor}>
					Unsorted
				</Text>
				<SortableContext items={rankState.pool} strategy={horizontalListSortingStrategy}>
					<DroppableRow id={DROP_POOL} itemCount={rankState.pool.length} bg={rowBg} borderColor={borderColor}>
						{rankState.pool.map((id) => (
							<SortableGameTile key={id} id={id} item={itemsById[id]} />
						))}
					</DroppableRow>
				</SortableContext>
			</Box>

			{TIER_META.map(({ key, label, colorScheme }) => (
				<Box key={key} mb={{ base: 3, md: 4 }}>
					<Flex align="center" gap={2} mb={2}>
						<Badge colorScheme={colorScheme} fontSize="0.75em" px={2} py={0.5} rounded="md">
							{label}
						</Badge>
						{key === 'S' ? (
							<Text fontSize="xs" color={rowLabelColor}>
								Best
							</Text>
						) : key === 'F' ? (
							<Text fontSize="xs" color={rowLabelColor}>
								Lowest
							</Text>
						) : null}
					</Flex>
					<SortableContext items={rankState.tiers[key]} strategy={horizontalListSortingStrategy}>
						<DroppableRow id={dropIdForTier(key)} itemCount={rankState.tiers[key].length} bg={rowBg} borderColor={borderColor}>
							{rankState.tiers[key].map((id) => (
								<SortableGameTile key={id} id={id} item={itemsById[id]} />
							))}
						</DroppableRow>
					</SortableContext>
				</Box>
			))}

			<Box mb={1}>
				<Text fontWeight="600" fontSize="sm" mb={2} color={rowLabelColor}>
					Excluded
				</Text>
				<Text fontSize="xs" color={rowLabelColor} mb={2}>
					Games here are hidden from the tier table in step 3.
				</Text>
				<SortableContext items={rankState.excluded} strategy={horizontalListSortingStrategy}>
					<DroppableRow id={DROP_EXCLUDED} itemCount={rankState.excluded.length} bg={rowBg} borderColor={borderColor}>
						{rankState.excluded.map((id) => (
							<SortableGameTile key={id} id={id} item={itemsById[id]} />
						))}
					</DroppableRow>
				</SortableContext>
			</Box>

			<DragOverlay adjustScale={false}>{activeItem ? <GameTilePreview item={activeItem} /> : null}</DragOverlay>
		</DndContext>
	)
}
